import React, { useEffect } from 'react'
import { X, CheckCircle, AlertCircle, Info } from 'lucide-react'

export default function Modal({ type = 'info', message, onClose, autoClose = 0 }) {
  // Si autoClose > 0, fermer automatiquement
  useEffect(() => {
    if (autoClose > 0) {
      const timer = setTimeout(onClose, autoClose)
      return () => clearTimeout(timer)
    }
  }, [autoClose, onClose])

  // Configuration des types avec couleurs et icônes
  const typeConfig = {
    info: {
      bg: 'bg-gradient-to-br from-blue-50 to-blue-100',
      border: 'border-blue-200',
      icon: <Info className="w-6 h-6 text-blue-600" />,
      iconBg: 'bg-blue-100',
      button: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500',
      title: 'Information',
      titleColor: 'text-blue-800'
    },
    success: {
      bg: 'bg-gradient-to-br from-emerald-50 to-emerald-100',
      border: 'border-emerald-200',
      icon: <CheckCircle className="w-6 h-6 text-emerald-600" />,
      iconBg: 'bg-emerald-100',
      button: 'bg-emerald-600 hover:bg-emerald-700 focus:ring-emerald-500',
      title: 'Succès',
      titleColor: 'text-emerald-800'
    },
    error: {
      bg: 'bg-gradient-to-br from-red-50 to-red-100',
      border: 'border-red-200',
      icon: <AlertCircle className="w-6 h-6 text-red-600" />,
      iconBg: 'bg-red-100',
      button: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
      title: 'Erreur',
      titleColor: 'text-red-800'
    }
  }

  const config = typeConfig[type]

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop amélioré */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-md transition-opacity duration-300"
        onClick={onClose}
      />
      
      {/* Modale avec animation */}
      <div className={`
        relative z-10 max-w-md w-full rounded-3xl shadow-2xl border-2 
        ${config.bg} ${config.border}
        transform transition-all duration-300 scale-100
        animate-in fade-in-0 zoom-in-95 duration-300
      `}>
        {/* Header avec icône */}
        <div className="flex items-center justify-between p-6 pb-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-full ${config.iconBg}`}>
              {config.icon}
            </div>
            <h3 className={`text-lg font-semibold ${config.titleColor}`}>
              {config.title}
            </h3>
          </div>
          
          {/* Bouton fermer (X) */}
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-white/20 transition-colors duration-200 group"
          >
            <X className="w-5 h-5 text-gray-500 group-hover:text-gray-700" />
          </button>
        </div>
        
        {/* Contenu */}
        <div className="px-6 pb-6">
          <p className="text-gray-700 leading-relaxed mb-6">
            {message}
          </p>
          
          {/* Bouton d'action */}
          <button
            onClick={onClose}
            className={`
              w-full px-6 py-3 rounded-xl text-white font-medium
              ${config.button}
              transition-all duration-200
              focus:outline-none focus:ring-2 focus:ring-offset-2
              transform hover:scale-[1.02] active:scale-[0.98]
              shadow-lg hover:shadow-xl
            `}
          >
            Fermer
          </button>
        </div>
        
        {/* Indicateur de fermeture automatique */}
        {autoClose > 0 && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200 rounded-b-3xl overflow-hidden">
            <div 
              className="h-full bg-gray-400 rounded-b-3xl transition-all ease-linear"
              style={{
                animation: `shrink ${autoClose}ms linear forwards`
              }}
            />
          </div>
        )}
      </div>
      
      {/* Styles CSS pour l'animation */}
      <style jsx>{`
        @keyframes shrink {
          from { width: 100%; }
          to { width: 0%; }
        }
        
        @keyframes animate-in {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        .animate-in {
          animation: animate-in 0.3s ease-out;
        }
      `}</style>
    </div>
  )
}